import { supabase } from '@/lib/supabase';
import type { Community } from '@/types';

/**
 * Data layer for the My Communities page (bottom-nav / sidebar → Communities).
 * Aggregates, for the signed-in user: every community they own or are a member
 * of, plus the latest message, the latest announcement/PSA, and a small member
 * face-pile per community. Uses the same live tables as the OG:
 * `community_members`, `communities`, `community_messages`, `profiles`.
 */

export type MyRelationship = 'owner' | 'member';

export interface MyCommunityMessage {
  body: string;
  authorName: string | null;
  createdAt: string;
  /** True when the latest message landed in an announcements channel. */
  isAnnouncement: boolean;
}

export interface MyCommunityAnnouncement {
  body: string;
  createdAt: string;
}

export interface MemberFace {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface MyCommunity {
  community: Community;
  relationship: MyRelationship;
  isPrivate: boolean;
  joinedAt: string | null;
  founding: boolean;
  subscriptionStatus: string | null;
  /** Up to a handful of members for the avatar face-pile. */
  members: MemberFace[];
  /** Newest message in any channel — the "latest messages" signal. */
  lastMessage: MyCommunityMessage | null;
  /** Newest message in an announcements channel — the "PSA" signal. */
  announcement: MyCommunityAnnouncement | null;
  /** max(last message, community creation) — drives the default activity sort. */
  lastActivityAt: string;
}

/** Channel ids the OG treats as the community's announcement/PSA feed. */
const ANNOUNCEMENT_CHANNELS = new Set(['announcements', 'announcement', 'psa']);
const SNIPPET_MAX = 120;
const FACES_PER_COMMUNITY = 5;

function snippet(body: string | null): string {
  const text = (body ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > SNIPPET_MAX ? `${text.slice(0, SNIPPET_MAX - 1)}…` : text;
}

const isAnnouncementChannel = (channelId: string | null): boolean =>
  ANNOUNCEMENT_CHANNELS.has((channelId ?? '').toLowerCase());

export async function listMyCommunities(userId: string): Promise<MyCommunity[]> {
  if (!userId) return [];

  // 1. Memberships + owned communities in parallel — independent reads.
  const [membershipsRes, ownedRes] = await Promise.all([
    supabase
      .from('community_members')
      .select('community_id, joined_at, founding, subscription_status')
      .eq('user_id', userId),
    supabase.from('communities').select('*').eq('owner_id', userId),
  ]);
  if (membershipsRes.error) throw membershipsRes.error;
  if (ownedRes.error) throw ownedRes.error;

  const memberships = membershipsRes.data ?? [];
  const owned = ownedRes.data ?? [];
  const ownedIds = new Set(owned.map((c) => c.id));

  // 2. Resolve community rows for memberships we don't already have from `owned`.
  const membershipIds = memberships
    .map((m) => m.community_id)
    .filter((id) => !ownedIds.has(id));
  let memberCommunities: Community[] = [];
  if (membershipIds.length) {
    const { data, error } = await supabase.from('communities').select('*').in('id', membershipIds);
    if (error) throw error;
    memberCommunities = data ?? [];
  }

  const communityById = new Map<string, Community>();
  for (const c of [...owned, ...memberCommunities]) communityById.set(c.id, c);
  const allIds = [...communityById.keys()];
  if (!allIds.length) return [];

  // 3. Recent messages + member faces, in parallel. Both are bulk reads across
  //    all my communities, reduced per-community client-side.
  const [msgRes, faceRes] = await Promise.all([
    supabase
      .from('community_messages')
      .select('community_id, channel_id, body, author_id, created_at')
      .in('community_id', allIds)
      .order('created_at', { ascending: false })
      .limit(400),
    supabase
      .from('community_members')
      .select('community_id, user_id, member_number')
      .in('community_id', allIds)
      .order('member_number', { ascending: true })
      .limit(Math.min(allIds.length * FACES_PER_COMMUNITY * 3, 300)),
  ]);
  if (msgRes.error) throw msgRes.error;
  if (faceRes.error) throw faceRes.error;
  const messages = msgRes.data ?? [];
  const faceRows = faceRes.data ?? [];

  // 4. One profiles read for every name/avatar we need (authors + faces).
  const neededIds = new Set<string>();
  for (const m of messages) neededIds.add(m.author_id);
  for (const f of faceRows) neededIds.add(f.user_id);
  const profileById = new Map<string, { name: string; avatarUrl: string | null }>();
  if (neededIds.size) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, handle, avatar_url')
      .in('id', [...neededIds]);
    for (const p of profiles ?? []) {
      profileById.set(p.id, {
        name: p.display_name || p.handle || 'Member',
        avatarUrl: p.avatar_url,
      });
    }
  }

  type MsgRow = (typeof messages)[number];
  const latestByCommunity = new Map<string, MsgRow>();
  const latestAnnByCommunity = new Map<string, MsgRow>();
  for (const m of messages) {
    if (!latestByCommunity.has(m.community_id)) latestByCommunity.set(m.community_id, m);
    if (isAnnouncementChannel(m.channel_id) && !latestAnnByCommunity.has(m.community_id)) {
      latestAnnByCommunity.set(m.community_id, m);
    }
  }

  const facesByCommunity = new Map<string, MemberFace[]>();
  for (const f of faceRows) {
    const list = facesByCommunity.get(f.community_id) ?? [];
    if (list.length >= FACES_PER_COMMUNITY) continue;
    const profile = profileById.get(f.user_id);
    list.push({ id: f.user_id, name: profile?.name ?? 'Member', avatarUrl: profile?.avatarUrl ?? null });
    facesByCommunity.set(f.community_id, list);
  }

  const membershipByCommunity = new Map(memberships.map((m) => [m.community_id, m]));

  const result: MyCommunity[] = allIds.map((id) => {
    const community = communityById.get(id)!;
    const membership = membershipByCommunity.get(id) ?? null;
    const latest = latestByCommunity.get(id) ?? null;
    const ann = latestAnnByCommunity.get(id) ?? null;

    const lastMessage: MyCommunityMessage | null = latest
      ? {
          body: snippet(latest.body),
          authorName: profileById.get(latest.author_id)?.name ?? null,
          createdAt: latest.created_at,
          isAnnouncement: isAnnouncementChannel(latest.channel_id),
        }
      : null;

    return {
      community,
      relationship: ownedIds.has(id) ? 'owner' : 'member',
      isPrivate: community.privacy === 'private',
      joinedAt: membership?.joined_at ?? null,
      founding: membership?.founding ?? false,
      subscriptionStatus: membership?.subscription_status ?? null,
      members: facesByCommunity.get(id) ?? [],
      lastMessage,
      announcement: ann ? { body: snippet(ann.body), createdAt: ann.created_at } : null,
      lastActivityAt: latest?.created_at ?? community.created_at,
    };
  });

  // Most recent activity first (page can re-sort).
  result.sort(
    (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime(),
  );
  return result;
}
